/**
 * When to warn a tester that the phone is about to be taken back.
 *
 * The service releases a phone after ten minutes with no action from a person (`SESSION_IDLE_S`) —
 * polling no longer counts, since an open tab used to hold a phone for ever that way (ported from
 * Device Lab, 2026-09-23). So the timeout is real, which means it will sometimes fire on somebody who
 * is still there: reading a long log, thinking about the next step of a scenario.
 *
 * Hence a warning, and an explicit way to say "still here". Deliberately **not** a timer that renews by
 * itself: a tab renewing its own claim is exactly what used to hold a phone for ever, and rebuilding it
 * on the client would be the same bug one layer up.
 *
 * Pure so the arithmetic is testable without a browser. The mistake to avoid is a banner that appears
 * while somebody is actively tapping — cry wolf once and the real warning gets ignored.
 */

/** Matches the service's `SESSION_IDLE_S`. If they drift, the warning is wrong in one direction or the
 *  other: too early is noise, too late is a phone that vanishes without notice. */
export const IDLE_LIMIT_MS = 600_000

/** How long before the deadline to speak up. Long enough to notice and act, short enough that it is
 *  not on screen through normal work. */
export const WARN_BEFORE_MS = 90_000

export type HoldState =
  | { state: 'held' }
  /** the phone frees itself in `inS` seconds unless something happens */
  | { state: 'warning'; inS: number }
  /** past the limit: the service may already have given it away */
  | { state: 'lapsed' }

/** `lastActionAt` is when the tester last did something the *service* counts — a tap, a key, an
 *  install, switching the log on, or an explicit renewal. Not a poll, not a frame arriving. */
export function holdState(lastActionAt: number, now: number,
                          limitMs = IDLE_LIMIT_MS, warnMs = WARN_BEFORE_MS): HoldState {
  const left = limitMs - (now - lastActionAt)
  if (left <= 0) return { state: 'lapsed' }
  if (left <= warnMs) return { state: 'warning', inS: Math.ceil(left / 1000) }
  return { state: 'held' }
}

/** Whole minutes until the idle release, rounded up — what the session bar shows while nothing is
 *  urgent (redesign stage 2, from Device Lab). Rounded up so it never says "0 min" while held; the last
 *  ninety seconds belong to the warning banner, which counts seconds. */
export function holdMinutesLeft(lastActionAt: number, now: number, limitMs = IDLE_LIMIT_MS): number {
  return Math.max(0, Math.ceil((limitMs - (now - lastActionAt)) / 60_000))
}

/** Why the service says this session no longer owns the phone. */
export type LostReason = 'session_gone' | 'session_lost' | 'preempted_by_post'

/** Did the service tell us this session no longer owns the phone — and why?
 *
 * Three shapes, because there are three ways it happens and the service is honest about all of them:
 * the session was ended when somebody else took the released phone (`session_gone`, a 404); it still
 * exists but the claim moved — a test run takes a phone without ending anybody's session
 * (`session_lost`, a 409); or automatic posting took the phone, which is the one thing that may end a
 * person's session (`preempted_by_post`, a 409 on every session-scoped call; 2026-10-08). Either way the
 * tester needs to be told the phone is not theirs, not shown a raw error — and for a posting, that it
 * was a priority task, not a colleague. `null` means something else went wrong.
 */
export function lostThePhone(status: number, detail: unknown): LostReason | null {
  const error = (detail as { detail?: { error?: string } } | null)?.detail?.error
  if (status === 404 && error === 'session_gone') return 'session_gone'
  if (status === 409 && (error === 'session_lost' || error === 'preempted_by_post')) return error
  return null
}

/** Does the panel that got this `session:start` answer own the claim, i.e. may it stop the session?
 *  A fresh claim is its own; a reattached one only when the service says this very tab took it
 *  (`took_here`: a reload). An older service without the field answers as before: never for a reattach. */
export function ownsClaimOf(started: { reattached?: boolean; took_here?: boolean }): boolean {
  return !started.reattached || started.took_here === true
}
